import { useCurrentUserStore } from "@stores/currentUser";
import { storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import { useSectionsStore } from "@stores/survey/sections";
import { useCardTypesStore } from "@stores/survey/cardTypes";
import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";
import { useSurveyQuestionsStore } from "@stores/surveyQuestions";
import { useSurveysStore } from "@stores/surveys";
import { useUsersStore } from "@stores/users";
import { useAccountsStore } from "@stores/accounts";
import { useMCampaignsStore } from "@stores/mCampaigns";
import { useMCampaignStore } from "@stores/mCampaign";
import { useJobsStore } from "@stores/job/jobs";
import { usePricerProjectsStore } from "@stores/pricer/pricerProjects";
import { usePricerProjectStore } from "@stores/pricer/pricerProject";
import { usePricerHubSpotStore } from "@stores/pricer/pricerHubSpot";
import { useMailingListsStore } from "@stores/survey/mailing/mailingLists";
import { useMailingListStore } from "@stores/survey/mailing/mailingList";
import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";
import { useSurveyLearningsStore } from "@stores/survey/surveyLearnings";
import { useEmailCampaignsStore } from "@stores/survey/mailing/emailCampaigns";
import { useEmailCampaignStore } from "@stores/survey/mailing/emailCampaign";
import { useExportsStore } from "@stores/survey/exports";
import { useTemplatesStore } from "@stores/mailing/templates";
import { useTemplateStore } from "@stores/mailing/template";
import { useContactAttributesStore } from "@stores/survey/mailing/contactAttributes";
import { useContactsStore } from "@stores/survey/mailing/contacts";
import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
import { useDefaultMessagesStore } from "@stores/survey/defaultMessages";
import { hasSurveysUrlFilters } from "@composables/filters/useSurveysFilters";
import { UnauthorizedError, ForbiddenError, NotFoundError } from "./errors";

const checkIsLoggedIn = () => {
  const { isLoggedIn } = useCurrentUserStore();
  if (!isLoggedIn) throw new Error("User not logged in");
};

// A route is allowed when the user type is listed in meta.userTypes, when one of
// the server-resolved boolean flags of the user payload listed in meta.userFlags
// is true (e.g. "jcd_access?"), or when the user holds any of the accesses it
// requires. `meta.accountAccesses` demands the access at account level — a role
// granted on a single survey does not satisfy it, which is what account-scoped
// pages (report themes, contracts, members, creating a survey) need.
export const isRouteAllowed = (meta) => {
  const { user, getUserType, hasAnyAccess, hasAccountAccess } = useCurrentUserStore();
  const { userTypes = [], userFlags = [], accesses = [], accountAccesses = [] } = meta;

  if (userTypes.includes(getUserType)) return true;
  if (userFlags.some((flag) => user?.[flag])) return true;
  if (accountAccesses.length > 0) return accountAccesses.some((access) => hasAccountAccess(access));

  return accesses.length > 0 && hasAnyAccess(accesses);
};

const checkUserAllowed = (to) => {
  if (!isRouteAllowed(to.meta)) throw new ForbiddenError("User not allowed");
  return true;
};

const fetchSurveyForGuards = async (token) => {
  const surveyStore = useSurveyStore();
  if (surveyStore.token === token) return;
  try {
    await surveyStore.fetch(token);
  } catch (error) {
    const { isLoggedIn } = useCurrentUserStore();
    if (isLoggedIn && error instanceof ForbiddenError) throw new NotFoundError("Survey not found");
    throw error;
  }
};

const checkSurveyAccount = async (to) => {
  // This guard checks the user reaches this particular survey — through an
  // account role or a role granted on the survey itself. `meta.accesses` was
  // only matched against the user's global accesses, which a role held on
  // another survey is enough to satisfy, so re-check them against the accesses
  // the API resolved for this survey.
  const { params: { token } = {} } = to;
  const surveyStore = useSurveyStore();
  await fetchSurveyForGuards(token);
  const { isLoggedIn, isAdmin, isSale } = useCurrentUserStore();
  const { accesses = [] } = to.meta;

  const allowedOnThisSurvey =
    accesses.length === 0 || accesses.some((access) => surveyStore.surveyAccesses.includes(access));

  if (isLoggedIn && (isAdmin || (isSale && to.meta.noAccountCheckForSales) || allowedOnThisSurvey)) return true;

  throw new NotFoundError("Survey not found");
};

const checkBrevoAccess = async (to) => {
  const { params: { token } = {} } = to;
  const surveyStore = useSurveyStore();
  const { isAdmin, withBrevoAccess } = storeToRefs(useCurrentUserStore());
  if (["TemplateNew", "TemplateEdit", "TemplateSettings", "Templates"].includes(to.name)) {
    if (!withBrevoAccess.value) throw new ForbiddenError("User not allowed");
    return true;
  }
  await fetchSurveyForGuards(token);
  if (!surveyStore.survey.has_mailing_access && !isAdmin.value) throw new ForbiddenError("User not allowed");
  return true;
};

let lastTargetPath = null;

export const rememberRoutingTarget = (to) => {
  lastTargetPath = to?.fullPath ?? null;
};

const buildLoginRedirectQuery = (to) => {
  const path = to?.fullPath ?? lastTargetPath;
  // Don't loop back to login/auth pages or the home page.
  if (!path || path === "/" || path.startsWith("/login")) return undefined;
  return { redirect: path };
};

export const handleRoutingError = (error, to) => {
  if (error instanceof NotFoundError) return { name: "NotFound", params: { catchAll: "not_found" } };

  // Forbidden page only for logged-in users; for anonymous a 403 means "log in first".
  const currentUserStore = useCurrentUserStore();
  if (currentUserStore.isLoggedIn && error instanceof ForbiddenError) return { name: "Forbidden" };

  // Otherwise it's an auth problem (not logged in, or a 401 from an expired session):
  // go to login, keeping the target URL to return to after sign-in.
  if (error instanceof UnauthorizedError) currentUserStore.user = null; // clear stale session
  return { name: "Login", query: buildLoginRedirectQuery(to) };
};

export const handleUser = async (to) => {
  if (!to.meta.requiresAuth) return true;

  const { fetch } = useCurrentUserStore();
  const { isLoggedIn } = useCurrentUserStore();
  if (!isLoggedIn) await fetch();
  checkIsLoggedIn();
  return true;
};

export const checkUserAccess = async (to) => {
  if (to.meta.requiresAuth) checkUserAllowed(to);
  if (to.meta.checkSurveyAccount) await checkSurveyAccount(to);
  if (to.meta.checkBrevoAccess) await checkBrevoAccess(to);

  return true;
};

export const fetchSurveyResources = async (to) => {
  const { params: { token } = {} } = to;
  const { isAdmin, isSale, isLoggedIn } = useCurrentUserStore();
  const surveyStore = useSurveyStore();
  await surveyStore.fetch(token);
  await Promise.all([useSectionsStore().fetch(), useCardTypesStore().fetch(token)]);
  if (isAdmin || isSale) {
    await useUsersStore().fetch({ userTypes: ["admin", "super_admin"], per_page: 50 });
    await useSurveyProfilesStore().fetch(token);
  }
  if (isLoggedIn) await useAccountsStore().fetch();
  await useSurveyTargetsStore().fetch(token);
  await useSurveyQuestionsStore().setQuestions();

  return true;
};

export const surveyLandingRoute = async (to) => {
  await fetchSurveyForGuards(to.params.token);
  const { canWriteSurvey, canAnalyzeSurvey } = useSurveyStore();
  return canWriteSurvey || canAnalyzeSurvey ? "SurveyQuestions" : "SurveyProgress";
};

export const handleSurveys = async (to) => {
  const accountsStore = useAccountsStore();
  const surveysStore = useSurveysStore();
  await accountsStore.fetch();
  if (!hasSurveysUrlFilters(to?.query || {})) await surveysStore.fetch();
  return true;
};

export const handleLearnings = async (route) => {
  const { isAdmin } = useCurrentUserStore();
  if (isAdmin) {
    const { params: { token } = {} } = route;
    const learningsStore = useSurveyLearningsStore();
    await learningsStore.fetchLearnings(token);
  }
  return true;
};

export const setTitlePage = (to) => {
  const { meta } = to;
  if (meta.name === "survey") {
    const surveyStore = useSurveyStore();
    const { surveyName } = storeToRefs(surveyStore);
    meta.title = `${meta.title} | ${surveyName.value}`;
  }
  if (meta.name === "ads") {
    const mCampaignStore = useMCampaignStore();
    const { nameMCampaign } = storeToRefs(mCampaignStore);
    meta.title = `${meta.title} | ${nameMCampaign.value}`;
  }
  if (meta.name === "template") {
    const templateStore = useTemplateStore();
    const { template } = storeToRefs(templateStore);
    meta.title = `${meta.title} | ${template.value.name}`;
  }
  if (meta.name === "pricer project") {
    const pricerProjectStore = usePricerProjectStore();
    const { project } = storeToRefs(pricerProjectStore);
    meta.title = `${meta.title} | ${project.value.name}`;
  }
};

export const handleMCampaigns = async () => {
  const { isAdmin } = useCurrentUserStore();
  const usersStore = useUsersStore();
  const accountsStore = useAccountsStore();

  if (isAdmin) {
    await usersStore.fetch();
    await accountsStore.fetch();
  }
  const mCampaignsStore = useMCampaignsStore();
  await mCampaignsStore.fetchMCampaigns();

  return true;
};

export const handleMCampaign = async (route) => {
  const { params: { token } = {} } = route;
  const { fetchMCampaign } = useMCampaignStore();
  await fetchMCampaign(token);
  return true;
};

export const handleJobs = async (create = false) => {
  const { isAdmin } = useCurrentUserStore();
  const usersStore = useUsersStore();
  const jobsStore = useJobsStore();

  if (isAdmin) await usersStore.fetch({ userTypes: ["admin", "super_admin"], per_page: 100 });
  await jobsStore.getJobsList();
  if (create) jobsStore.initRefOnCreate();

  return true;
};

export const handleJob = async (route) => {
  const { params: { id } = {} } = route;

  const { isAdmin } = useCurrentUserStore();
  const usersStore = useUsersStore();
  const jobsStore = useJobsStore();

  if (isAdmin) await usersStore.fetch({ userTypes: ["admin", "super_admin"], per_page: 100 });
  await jobsStore.getJob(id);

  return true;
};

export const handlePricer = async () => {
  const usersStore = useUsersStore();
  const pricerStore = usePricerProjectsStore();

  await usersStore.fetch({ userTypes: ["admin", "super_admin", "sales"], per_page: 100 });
  await pricerStore.fetch();
  return true;
};

export const handlePricerProject = async (route) => {
  const { params: { token } = {} } = route;

  const pricerProjectStore = usePricerProjectStore();
  const usersStore = useUsersStore();
  const hubSpotStore = usePricerHubSpotStore();

  await usersStore.fetch({ userTypes: ["admin", "super_admin", "sales"], per_page: 100 });
  await pricerProjectStore.fetch(token);
  hubSpotStore.fetchCompanies();
  return true;
};

export const handlePricerProjectNew = async () => {
  const usersStore = useUsersStore();
  const hubSpotStore = usePricerHubSpotStore();

  await usersStore.fetch({ userTypes: ["admin", "super_admin", "sales"], per_page: 100 });
  hubSpotStore.fetchCompanies();
  return true;
};

export const handleMailingLists = async () => {
  const mailingListsStore = useMailingListsStore();
  await mailingListsStore.getMailingLists();
  return true;
};

export const handleMailingList = async (route) => {
  const mailingListStore = useMailingListStore();
  const { params: { listToken } = {} } = route;

  if (route.name === "MailingListEdit") await mailingListStore.getMailingList(listToken);
  if (route.name === "MailingListNew") await mailingListStore.initMailingList();
  return true;
};

export const handleLightMailingLists = async () => {
  const mailingListsStore = useMailingListsStore();
  await mailingListsStore.getLightMailingLists();
  return true;
};

export const handleEmailCampaigns = async () => {
  const emailCampaignsStore = useEmailCampaignsStore();
  await emailCampaignsStore.getEmailCampaigns();
  return true;
};

export const handleEmailCampaign = async (route) => {
  const emailCampaignStore = useEmailCampaignStore();
  const { params: { campaignToken } = {} } = route;

  if (route.name === "EmailCampaignEdit") await emailCampaignStore.getEmailCampaign(campaignToken);
  if (route.name === "EmailCampaignNew") await emailCampaignStore.initEmailCampaign();
  return true;
};

export const handleSurveyExport = async (route) => {
  await useExportsStore().fetchExport(route.params.exportToken);
  return true;
};

export const handleTemplates = async (route) => {
  const templatesStore = useTemplatesStore();
  const currentUserStore = useCurrentUserStore();
  const accountsStore = useAccountsStore();
  const baseOpts = { active: [true], base: [true, false], perPage: 100 };

  if (["EmailCampaignNew", "EmailCampaignEdit"].includes(route.name)) {
    if (currentUserStore.isAdmin) {
      const { params: { token } = {} } = route;
      const surveyStore = useSurveyStore();
      if (surveyStore.token !== token) await surveyStore.fetch(token);
      const { surveyAccountId } = storeToRefs(surveyStore);
      await templatesStore.getTemplateOptions({ ...baseOpts, accounts: [surveyAccountId.value] });
    } else {
      await templatesStore.getTemplateOptions(baseOpts);
    }
  } else {
    if (currentUserStore.isLoggedIn) await accountsStore.fetch();
    await templatesStore.getTemplates();
  }
  return true;
};

export const handleTemplate = async (route) => {
  const { params: { token } = {} } = route;
  const templateStore = useTemplateStore();
  await templateStore.getTemplate(token);
  if (route.name === "TemplateSettings") {
    const accountsStore = useAccountsStore();
    await accountsStore.fetch();
  }
  return true;
};

export const handleContacts = async () => {
  const contactsStore = useContactsStore();
  await contactsStore.getContacts();
  return true;
};

export const handleAiInsightConversations = async () => {
  const { isLiveOrComplete } = storeToRefs(useSurveyStore());
  const aiInsightConversationsStore = useAiInsightConversationsStore();

  if (isLiveOrComplete.value) await aiInsightConversationsStore.getConversations();
  return true;
};

export const handleDefaultMessages = async () => {
  const store = useDefaultMessagesStore();
  await store.fetchAll();
  return true;
};

export const handleContactAttributes = async (route) => {
  const { params: { token } = {} } = route;

  const contactAttributesStore = useContactAttributesStore();
  await contactAttributesStore.fetchAttributes(token);

  return true;
};
